// ImportTree — collapsible tree component for navigating import payload.
// Left panel in the review state. Renders section headers with item counts,
// expand/collapse, item selection, error indicators, and remove buttons.

import { useState, useCallback, useMemo } from 'react'
import {
  ChevronRight,
  ChevronDown,
  AlertTriangle,
  Trash2,
  FileText,
  Package,
} from 'lucide-react'
import { Button } from '@/components/ui/button'
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip'
import { cn } from '@/lib/utils'
import type { TreeNode, DependencyMap } from './importTree'

// --- Props ---

interface ImportTreeProps {
  tree: TreeNode[]
  dependencyMap: DependencyMap
  selectedId: string | null
  onSelect: (node: TreeNode) => void
  onRemove: (node: TreeNode) => void
}

// --- Component ---

export function ImportTree({
  tree,
  dependencyMap,
  selectedId,
  onSelect,
  onRemove,
}: ImportTreeProps) {
  // Auto-expand: top-level groups + sections with items or errors
  const [expanded, setExpanded] = useState<Set<string>>(() => {
    const initial = new Set<string>()
    for (const node of tree) {
      // Always expand top-level groups
      initial.add(node.id)
      // Expand subsections that have items or errors
      for (const child of node.children) {
        if (child.type === 'section' && (child.children.length > 0 || child.hasErrors)) {
          initial.add(child.id)
        }
      }
    }
    return initial
  })

  const toggle = useCallback((id: string) => {
    setExpanded((prev) => {
      const next = new Set(prev)
      if (next.has(id)) {
        next.delete(id)
      } else {
        next.add(id)
      }
      return next
    })
  }, [])

  return (
    <div className="space-y-0.5 text-sm">
      {tree.map((node) => (
        <TreeNodeRow
          key={node.id}
          node={node}
          depth={0}
          expanded={expanded}
          selectedId={selectedId}
          dependencyMap={dependencyMap}
          onToggle={toggle}
          onSelect={onSelect}
          onRemove={onRemove}
        />
      ))}
    </div>
  )
}

// --- Tree node row ---

interface TreeNodeRowProps {
  node: TreeNode
  depth: number
  expanded: Set<string>
  selectedId: string | null
  dependencyMap: DependencyMap
  onToggle: (id: string) => void
  onSelect: (node: TreeNode) => void
  onRemove: (node: TreeNode) => void
}

function TreeNodeRow({
  node,
  depth,
  expanded,
  selectedId,
  dependencyMap,
  onToggle,
  onSelect,
  onRemove,
}: TreeNodeRowProps) {
  const isExpanded = expanded.has(node.id)
  const isSelected = selectedId === node.id
  const hasChildren = node.children.length > 0

  // Count items in this section (direct + nested)
  const itemCount = useMemo(() => {
    let count = 0
    function walk(nodes: TreeNode[]) {
      for (const n of nodes) {
        if (n.type === 'item') count++
        walk(n.children)
      }
    }
    walk(node.children)
    return count
  }, [node.children])

  // Check dependents for remove warning
  const dependents = node.type === 'item' ? dependencyMap.get(node.token) : undefined
  const hasDependents = dependents && dependents.size > 0

  if (node.type === 'section') {
    return (
      <>
        <div
          className={cn(
            'flex cursor-pointer items-center gap-1 rounded px-1 py-1 hover:bg-accent/50',
            depth === 0 && 'font-medium',
            depth === 1 && 'text-muted-foreground',
          )}
          style={{ paddingLeft: `${depth * 16 + 4}px` }}
          onClick={() => {
            if (hasChildren) onToggle(node.id)
          }}
        >
          {/* Expand/collapse chevron */}
          {hasChildren ? (
            isExpanded ? (
              <ChevronDown className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
            ) : (
              <ChevronRight className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
            )
          ) : (
            <span className="h-3.5 w-3.5 shrink-0" />
          )}

          {/* Section icon */}
          {depth === 0 ? (
            <Package className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
          ) : null}

          {/* Label */}
          <span className="truncate">{node.label}</span>

          {/* Item count badge */}
          {itemCount > 0 && (
            <span className="ml-auto shrink-0 rounded-full bg-muted px-1.5 py-0.5 text-[10px] font-medium text-muted-foreground">
              {itemCount}
            </span>
          )}

          {/* Error indicator */}
          {node.hasErrors && (
            <Tooltip>
              <TooltipTrigger asChild>
                <AlertTriangle className="ml-1 h-3.5 w-3.5 shrink-0 text-destructive" />
              </TooltipTrigger>
              <TooltipContent side="right">
                <p>Contains validation errors</p>
              </TooltipContent>
            </Tooltip>
          )}
        </div>

        {/* Children */}
        {isExpanded &&
          node.children.map((child) => (
            <TreeNodeRow
              key={child.id}
              node={child}
              depth={depth + 1}
              expanded={expanded}
              selectedId={selectedId}
              dependencyMap={dependencyMap}
              onToggle={onToggle}
              onSelect={onSelect}
              onRemove={onRemove}
            />
          ))}
      </>
    )
  }

  // Item node
  return (
    <div
      className={cn(
        'group flex cursor-pointer items-center gap-1 rounded px-1 py-0.5 hover:bg-accent/50',
        isSelected && 'bg-primary/10 text-primary',
      )}
      style={{ paddingLeft: `${depth * 16 + 4}px` }}
      onClick={() => onSelect(node)}
    >
      <FileText className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />

      <span className="truncate">{node.label}</span>

      {/* Token badge (if label differs from token) */}
      {node.label !== node.token && node.token && (
        <span className="ml-1 shrink-0 truncate text-[10px] text-muted-foreground">
          ({node.token})
        </span>
      )}

      {/* Error indicator */}
      {node.hasErrors && (
        <Tooltip>
          <TooltipTrigger asChild>
            <AlertTriangle className="ml-auto h-3.5 w-3.5 shrink-0 text-destructive" />
          </TooltipTrigger>
          <TooltipContent side="right">
            <div className="max-w-xs space-y-0.5">
              {node.errors.map((err, i) => (
                <p key={i} className="text-xs">{err.message}</p>
              ))}
            </div>
          </TooltipContent>
        </Tooltip>
      )}

      {/* Remove button */}
      <Tooltip>
        <TooltipTrigger asChild>
          <Button
            variant="ghost"
            size="icon"
            className={cn(
              'ml-auto h-5 w-5 shrink-0 opacity-0 group-hover:opacity-100',
              hasDependents && 'text-amber-600 hover:text-amber-700',
              !hasDependents && 'text-muted-foreground hover:text-destructive',
            )}
            onClick={(e) => {
              e.stopPropagation()
              onRemove(node)
            }}
          >
            <Trash2 className="h-3 w-3" />
          </Button>
        </TooltipTrigger>
        <TooltipContent side="right">
          {hasDependents ? (
            <p className="text-xs">
              Remove (referenced by {dependents.size} item{dependents.size !== 1 ? 's' : ''}:{' '}
              {Array.from(dependents).slice(0, 3).join(', ')}
              {dependents.size > 3 ? '...' : ''})
            </p>
          ) : (
            <p className="text-xs">Remove from import</p>
          )}
        </TooltipContent>
      </Tooltip>
    </div>
  )
}
